import {FilenameUtil, HTMLUtil, ObjUtil, Util} from 'yougile-util'
import {ChatJSON} from './chat'
import {
  DataObject,
  DataObjectId,
  DataObjectJSON,
  DataObjectProperties
} from './data-object'

export interface MessageTextChunk {
  type: 'user'
  replacement: string
  data: {
    userId?: DataObjectId
  }
}

export interface MessageTextParams {
  chunks: MessageTextChunk[]
}

export const isMessageTextParams = (
  data: unknown
): data is MessageTextParams => {
  return !!data && (data as MessageTextParams).chunks instanceof Array
}

export const toMessageTextParams = (
  data: unknown
): MessageTextParams | undefined => {
  return isMessageTextParams(data) ? data : undefined
}

export const isUserMentionedInMessage = (
  userId: DataObjectId,
  msg: {properties?: any}
): boolean => {
  if (!msg?.properties?.params) {
    return false
  }
  const params = msg.properties.params as MessageTextParams
  if (!params.chunks) {
    return false
  }
  return !!params.chunks.find(
    c => c.type === 'user' && c.data && c.data.userId === userId
  )
}

export const getMentionedUsers = (msg: {properties?: any}): DataObjectId[] => {
  if (!msg?.properties?.params) {
    return []
  }
  const params = msg.properties.params as MessageTextParams
  if (!params.chunks) {
    return []
  }
  const res: Set<DataObjectId> = new Set()
  params.chunks.forEach(c => {
    if (c.type === 'user' && c.data && c.data.userId) {
      res.add(c.data.userId)
    }
  })
  return [...res]
}

export const smilies = [
  '👍',
  '👎',
  '👏',
  '🙂',
  '😀',
  '😕',
  '😱',
  '🎉',
  '❤',
  '🚀',
  '✔'
] as const
export type Smiley = (typeof smilies)[number]

export interface Reaction {
  smiley: Smiley
  timestamp: number
}

export interface DataObjectReactions {
  [userId: DataObjectId]: Reaction[]
}

const transcriptionState = ['inProgress', 'complete'] as const

export function isTranscriptionState(
  keyInput: unknown
): keyInput is TranscriptionState {
  return transcriptionState.includes(keyInput as TranscriptionState)
}

export const toTranscriptionState = (
  data: unknown
): TranscriptionState | undefined => {
  return isTranscriptionState(data) ? data : undefined
}

export type TranscriptionState = (typeof transcriptionState)[number]

export interface ChatMessageJSON extends DataObjectJSON {
  from: DataObjectId
  text: string
  label?: string
  edited?: {timestamp: number}
  apiData?: any
  reactions?: DataObjectReactions
  properties?: DataObjectProperties
}

export const differenceOnlyInLastSeenMessages = (
  c1: ChatJSON | undefined,
  c2: ChatJSON | undefined
): boolean => {
  if (!c1 || !c2) {
    return !c1 && !c2
  }
  return ObjUtil.deepEqual(
    {...c1, lastSeenMessages: {}},
    {...c2, lastSeenMessages: {}},
    true
  )
}

export const differenceOnlyInLabels = (
  c1: ChatJSON | undefined,
  c2: ChatJSON | undefined
): boolean => {
  if (!c1 || !c2) {
    return !c1 && !c2
  }
  if (!c1.messages || !c2.messages) {
    return !c1.messages && !c2.messages
  }
  if (!ObjUtil.deepEqual({...c1, messages: {}}, {...c2, messages: {}}, true)) {
    return false
  }
  if (
    !ObjUtil.deepEqual(Object.keys(c1.messages), Object.keys(c2.messages), true)
  ) {
    return false
  }
  for (const key of Object.keys(c1.messages)) {
    const m1 = Util.clone(c1.messages[+key])
    const m2 = Util.clone(c2.messages[+key])
    if (!m1 || !m2) {
      if (!!m1 !== !!m2) {
        return false
      }
    } else {
      m1.label = ''
      m2.label = ''
      if (!ObjUtil.deepEqual(m1, m2, true)) {
        return false
      }
    }
  }
  return true
}

export const isUserReaction = (
  oldReactions: DataObjectReactions | undefined,
  newReactions: DataObjectReactions | undefined,
  userId: DataObjectId
): boolean => {
  if (oldReactions && newReactions) {
    const usersWithChangedReactions = ObjUtil.keys(oldReactions).filter(
      key => oldReactions[key]?.length !== newReactions[key]?.length
    )
    return usersWithChangedReactions.every(el => el === userId)
  } else if (newReactions) {
    return Object.keys(newReactions).every(el => el === userId)
  } else if (oldReactions) {
    return Object.keys(oldReactions).every(el => el === userId)
  }
  return true
}

export type ChatMessageStatus = 'everybody' | 'nobody' | 'some'
// in obj ChatMessage.properties, voting field
export interface Voting {
  options: string[]
  multiSelectOn?: boolean
  votes?: {[userId: DataObjectId]: number[]}
}

export class ChatMessage extends DataObject {
  public from: DataObjectId
  public text: string
  public label: string
  public edited?: {timestamp: number}
  public apiData: any
  public reactions: DataObjectReactions = {}

  constructor(
    companyId: DataObjectId,
    from: DataObjectId,
    text: string,
    label: string,
    properties?: DataObjectProperties,
    reactions?: DataObjectReactions
  ) {
    super(companyId)
    this.dataType = 'ChatMessage'
    this.from = from
    this.text = text
    this.label = label || ''
    this.isLazy = false

    if (properties !== undefined) {
      this.properties = properties
    }

    if (reactions !== undefined) {
      this.reactions = reactions
    }
  }

  public override toJSON(): ChatMessageJSON {
    const res = super.toJSON() as ChatMessageJSON
    res.from = this.from
    res.text = this.text

    if (this.edited?.timestamp) {
      res.edited = Util.clone(this.edited)
    }

    if (this.label) {
      res.label = this.label
    }
    if (this.apiData) {
      res.apiData = Util.clone(this.apiData)
    }
    if (this.reactions) {
      res.reactions = this.reactions
    }
    return res
  }

  public override fromJSON(
    companyId: DataObjectId,
    data: ChatMessageJSON
  ): ChatMessage | undefined {
    if (!data.timestamp || !data.from || !data.text) {
      return undefined
    }
    super.fromJSON(companyId, data)
    this.from = data.from
    this.text = data.text + ''
    this.label = data.label || ''
    this.edited = data.edited
    this.apiData = Util.clone(data.apiData)
    this.reactions = data.reactions || {}
    return this
  }

  public getReplyTo(): number | undefined {
    return this.properties.replyTo ? +this.properties.replyTo : undefined
  }

  public setReplyTo(toMsg: ChatMessage): void {
    if (toMsg) {
      this.properties.replyTo = toMsg.timestamp
    }
  }

  public getParams(): MessageTextParams | undefined {
    return this.properties?.params as unknown as MessageTextParams | undefined
  }

  public isTextHtml(): boolean {
    return this.properties.textHtml !== undefined
  }

  public getTextHtml(): string {
    return (this.properties.textHtml as string | undefined) ?? ''
  }

  public setTextHtml(textHtml: string): void {
    this.properties.textHtml = textHtml
  }

  public clearTextHtml(): void {
    delete this.properties.textHtml
  }

  public getLinksForAttachments(): string[] {
    const rawStr = this.isTextHtml()
      ? HTMLUtil.sanitizeHtml(this.getTextHtml().trim())
      : HTMLUtil.escapeHtml(this.text.trim())

    const prefix = '/root/#file:/user-data/'
    const attachments: string[] = []
    for (const [file] of rawStr.matchAll(FilenameUtil.rx)) {
      if (file?.startsWith(prefix)) {
        const path = file.replace('/root/#file:', '')
        const uri = Util.safeDecodeUriComponent(path, path)
        attachments.push(uri)
      }
    }
    return attachments
  }
}

export type ChatMessageWithPropertiesJSON = Pick<
  ChatMessageJSON | ChatMessageJSON,
  'properties'
>
